'use client'

import { ChevronDown, ChevronRight, Leaf, Milk, Croissant, Beef, Package, Snowflake, Coffee, Cookie, Home, Sparkles, X } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { Card } from '@/components/ui/card'
import { useCategories, useProducts } from '@/hooks/useProducts'

interface CategoriesDropdownProps {
  onCategorySelect?: (categoryId: string) => void
}

// Map icon names to Lucide components
const iconMap: Record<string, any> = {
  'Leaf': Leaf,
  'Milk': Milk,
  'Croissant': Croissant,
  'Beef': Beef,
  'Package': Package,
  'Snowflake': Snowflake,
  'Coffee': Coffee,
  'Cookie': Cookie,
  'Home': Home,
  'Sparkles': Sparkles,
}

export default function CategoriesDropdown({ onCategorySelect }: CategoriesDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  
  const { data: categories, isLoading: categoriesLoading } = useCategories()
  const { data: allProducts, isLoading: productsLoading } = useProducts()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setExpandedCategory(null)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX
      })
    }
  }, [isOpen])

  const getCategoryProductCount = (categoryId: string) => {
    if (!allProducts?.products) return 0
    return allProducts.products.filter(p => p.category_id === categoryId).length
  }


  const handleCategoryClick = (categoryId: string) => {
    if (onCategorySelect) {
      onCategorySelect(categoryId)
    }
    setIsOpen(false)
    setExpandedCategory(null)
  }

  const toggleCategory = (categoryId: string) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId)
  }

  const getIconComponent = (iconName: string) => {
    const IconComponent = iconMap[iconName]
    return IconComponent ? <IconComponent className="h-5 w-5" /> : <Package className="h-5 w-5" />
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 text-sm font-medium text-foreground hover:text-primary whitespace-nowrap transition-colors"
      >
        All Categories
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Compact Categories Dropdown */}
      {isOpen && (
        <Card 
          className="fixed w-80 max-h-[500px] overflow-y-auto shadow-lg z-50 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-muted [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/50"
          style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }}
        >
          <div className="p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-3 pb-3 border-b border-border">
              <h3 className="font-semibold text-base">Categories</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Loading State */}
            {(categoriesLoading || productsLoading) && (
              <div className="text-center py-8 text-muted-foreground text-sm">
                <div className="animate-pulse">Loading...</div>
              </div>
            )}

            {/* No Categories */}
            {!categoriesLoading && !productsLoading && (!categories || categories.length === 0) && (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No categories available
              </div>
            )}

            {/* Categories List */}
            {!categoriesLoading && !productsLoading && categories && categories.length > 0 && (
              <div className="space-y-1">
                {categories.map((category) => {
                  const isExpanded = expandedCategory === category.id
                  const hasSubcategories = category.subcategories && category.subcategories.length > 0

                  return (
                    <div key={category.id} className="border-b border-border last:border-0">
                      {/* Category Header */}
                      <div className="flex items-center group">
                        <button
                          onClick={() => toggleCategory(category.id)}
                          className="flex-1 flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted transition-colors text-left"
                        >
                          <div className="text-primary flex-shrink-0">
                            {getIconComponent(category.icon)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-sm text-foreground">{category.name}</h4>
                            <p className="text-xs text-muted-foreground">
                              {getCategoryProductCount(category.id)} items
                            </p>
                          </div>
                          <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform flex-shrink-0 ${
                            isExpanded ? 'rotate-90' : ''
                          }`} />
                        </button>
                      </div>

                      {/* Expanded Subcategories */}
                      {isExpanded && hasSubcategories && (
                        <div className="pl-12 pr-2 pb-2 space-y-1">
                          {category.subcategories.map((sub: string, idx: number) => (
                            <button
                              key={idx}
                              onClick={() => handleCategoryClick(category.id)}
                              className="w-full text-left text-sm px-3 py-1.5 rounded hover:bg-muted text-foreground transition-colors"
                            >
                              {sub}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* View All Button - shown when expanded */}
                      {isExpanded && (
                        <div className="px-2 pb-2">
                          <button
                            onClick={() => handleCategoryClick(category.id)}
                            className="w-full py-2 text-sm font-medium text-primary hover:bg-primary/10 rounded transition-colors"
                          >
                            View All {category.name}
                          </button>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  )
}
